import React, { useState, useEffect } from 'react'
import styled from '@emotion/styled'
import { fontSize2XSmall } from '../../constants/principles'
import ColorSwatch from '../generic/colorSwatch'
import Engine from '../compiledSVG/icon/Engine'
import Transmission from '../compiledSVG/icon/Transmission'
import TwoWheelDrive from '../compiledSVG/icon/Twowheel'
import FourWheelDrive from '../compiledSVG/icon/Fourwheel'
import RwdIcon from '../compiledSVG/icon/Rwd'
import { highlightBlueBase } from '../../constants/principles'
import DoneModal from '../generic/modals/DoneModal'
import { ModalContentNormal } from '../generic/Basics.js'
import { cadsFlagsOfferVinDecodeMisMatch } from '../../utils/cadsUtils'
import { ArrayUtils } from '../../utils/siteUtils'

const map = {
  Red: '#DD4343',
  Burgundy: '#AB2431',
  Blue: '#1261A0',
  Black: '#202020',
  Gray: '#A0A0A0',
  Silver: 'linear-gradient(#D5D5D5, #AFAFAF)',
  Brown: '#6D462A',
  Green: '#006400',
  White: '#FDFDFD',
  Yellow: '#FEDD37',
  Gold: 'linear-gradient(#F4DF99, #E0C274)',
  Pink: '#F493B4',
  Beige: '#CFB48E',
  Orange: '#F79F1D',
  Purple: '#804F9D'
}
const svgsMaps = {
  '2wd': TwoWheelDrive,
  fwd: TwoWheelDrive,
  awd: FourWheelDrive,
  '4wd': FourWheelDrive,
  rwd: RwdIcon
}
const DrivetrainIcon = ({ name }) => {
  const Icon = svgsMaps[name.toLowerCase()] || TwoWheelDrive
  return <Icon color={map['Black']} />
}

export default ({ _engine, _transmission, _drivetrain, _color, _options = [], _genericOptions, setShowModal, vinResults, offer, prospect }) => {
  const [transmission, setTransmission] = useState(undefined)
  const [drivetrain, setDrivetrain] = useState(undefined)
  const [color, setColor] = useState(undefined)
  const [engine, setEngine] = useState(undefined)
  const [options, setOptions] = useState(undefined)
  const [genericOptions, setGenericOptions] = useState(undefined)
  const overrideVinResults = !cadsFlagsOfferVinDecodeMisMatch(offer, vinResults)

  useEffect(() => {
    setEngine(
      overrideVinResults && offer && offer.prospect.vehicle.vehicleBuildDataFlags && offer.prospect.vehicle.vehicleBuildDataFlags.enginesVinVerified
        ? _engine
        : vinResults && vinResults.enginesVinVerified && vinResults.engines
        ? vinResults.engines[0]
        : _engine
    )
    setTransmission(
      overrideVinResults && offer && offer.prospect.vehicle.vehicleBuildDataFlags && offer.prospect.vehicle.vehicleBuildDataFlags.transmissionsVinVerified
        ? _transmission
        : vinResults && vinResults.transmissionsVinVerified && vinResults.transmissions
        ? vinResults.transmissions[0]
        : _transmission
    )
    setDrivetrain(
      overrideVinResults && offer && offer.prospect.vehicle.vehicleBuildDataFlags && offer.prospect.vehicle.vehicleBuildDataFlags.drivetrainsVinVerified
        ? _drivetrain
        : vinResults && vinResults.drivetrainsVinVerified && vinResults.drivetrains
        ? vinResults.drivetrains[0]
        : _drivetrain
    )
    setColor(
      overrideVinResults && offer && offer.prospect.vehicle.vehicleBuildDataFlags && offer.prospect.vehicle.vehicleBuildDataFlags.colorsVinVerified ? _color : vinResults && vinResults.colorsVinVerified && vinResults.colors ? vinResults.colors[0] : _color
    )
    setOptions(overrideVinResults && offer && offer.prospect.vehicle.vehicleBuildDataFlags && offer.prospect.vehicle.vehicleBuildDataFlags.optionsVinVerified ? prospect && prospect.options : _options)
    setGenericOptions(_genericOptions)
  }, [])

  const optionList =
    genericOptions && genericOptions.length > 1
      ? ArrayUtils.deDuplicate(genericOptions).map((option, index) => <OptionDiv key={`opt${index}`}>{option}</OptionDiv>)
      : options && options.length > 1
      ? ArrayUtils.deDuplicate(options, 'displayName').map(option => <OptionDiv key={option.optionId}>{option.displayName}</OptionDiv>)
      : []
  let enginesVinVerified,
    transmissionsVinVerified,
    drivetrainsVinVerified,
    colorsVinVerified,
    optionsVinVerified = false
  if (vinResults) {
    enginesVinVerified = vinResults.enginesVinVerified
    transmissionsVinVerified = vinResults.transmissionsVinVerified
    drivetrainsVinVerified = vinResults.drivetrainsVinVerified
    colorsVinVerified = vinResults.colorsVinVerified
    optionsVinVerified = vinResults.optionsVinVerified
  }

  return (
    <>
      <StyledVehicleInfoDivGroup>
        {enginesVinVerified && engine && (
          <StyledVehicleInfoDisplayDiv>
            <StyledVehicleInfoIconDiv>
              <Engine color={map['Black']} />
            </StyledVehicleInfoIconDiv>
            <StyledVehicleInfoDiv data-testid={'vd-lb-engine'}>Engine:</StyledVehicleInfoDiv>
            <StyledVehicleInfoValueDiv>{engine.displayName}</StyledVehicleInfoValueDiv>
          </StyledVehicleInfoDisplayDiv>
        )}
        {transmissionsVinVerified && transmission && (
          <StyledVehicleInfoDisplayDiv>
            <StyledVehicleInfoIconDiv>
              <Transmission color={map['Black']} />
            </StyledVehicleInfoIconDiv>
            <StyledVehicleInfoDiv data-testid={'vd-lb-transmission'}>Transmission:</StyledVehicleInfoDiv>
            <StyledVehicleInfoValueDiv>{transmission.displayName}</StyledVehicleInfoValueDiv>
          </StyledVehicleInfoDisplayDiv>
        )}
        {drivetrainsVinVerified && drivetrain && (
          <StyledVehicleInfoDisplayDiv>
            <StyledVehicleInfoIconDiv>
              <DrivetrainIcon name={drivetrain.displayName} />{' '}
            </StyledVehicleInfoIconDiv>
            <StyledVehicleInfoDiv data-testid={'vd-lb-drivetrain'}>Drivetrain:</StyledVehicleInfoDiv>
            <StyledVehicleInfoValueDiv>{drivetrain.displayName}</StyledVehicleInfoValueDiv>
          </StyledVehicleInfoDisplayDiv>
        )}
        {colorsVinVerified && color && (
          <StyledVehicleInfoDisplayDiv>
            <StyledVehicleInfoIconDiv>
              <ColorSwatch isSelected={false} colorId={color.id} colorName={''} colorCode={map[color.displayName]} onClick={() => {}} size={64} />
            </StyledVehicleInfoIconDiv>
            <StyledVehicleInfoDiv data-testid={'vd-lb-color'}>Color:</StyledVehicleInfoDiv>
            <StyledVehicleInfoValueDiv>{color.displayName}</StyledVehicleInfoValueDiv>
          </StyledVehicleInfoDisplayDiv>
        )}
      </StyledVehicleInfoDivGroup>
      {optionsVinVerified && optionList && optionList.length > 1 && (
        <>
          <DoneModal testId={'vd-modal-details-feature-n-equipment'} name={'featuresAndEquipment'} title={'Features & Equipment'} subtitle={''} onClick={e => setShowModal(false)} informational={false}>
            <ModalContentNormal>
              <OptionsList data-testid={'vd-lst-all-featuresAndEquipment'}>{optionList}</OptionsList>
            </ModalContentNormal>
          </DoneModal>
          <StyledSpan data-testid={'vd-bt-see-all-featuresAndEquipment'} onClick={e => setShowModal('featuresAndEquipment')}>{'See all Features & Equipment'}</StyledSpan>
        </>
      )}
    </>
  )
}

const StyledVehicleInfoDivGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
`

const StyledVehicleInfoDisplayDiv = styled.div`
  width: 150px;
`

const StyledVehicleInfoIconDiv = styled.div`
  height: 64px;
  font-size: ${fontSize2XSmall};
  text-align: center;
`

const StyledVehicleInfoDiv = styled.div`
  font-size: ${fontSize2XSmall};
  text-align: center;
`

const StyledVehicleInfoValueDiv = styled.div`
  font-size: ${fontSize2XSmall};
  align-items: center;
  text-align: center;
  font-weight: bold;
  padding-bottom: 25px;
`

const StyledDiv = styled.div`
  text-align: center;
`

const OptionsList = styled.div`
  columns: 50px 2;
`

const OptionDiv = styled.div`
  font-weight: bold;
  text-align: left;
`

const StyledSpan = styled.span`
  margin-top: -8px;
  color: ${highlightBlueBase};
  font-size: ${fontSize2XSmall};
  cursor: pointer;
  :hover {
    text-decoration: underline;
  }
`
